<!-- =========================================================================================
  File Name: CustomerDebtListFormSidebar.vue
  Description: Add New Data - Sidebar component
========================================================================================== -->
<i18n src="@/locales/modules/CustomerTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="
			setDialogHide =>
				handleCloseDialog(() => {
					initValues();
					setDialogHide();
				}, isFormDirty)
		"
		class="custom-dialog todo-list-form-sidebar"
		:visible.sync="isSidebarActiveLocal"
	>
		<div slot="title">
			<h4 class="uppercase font-bold block text-xl w-full">{{ $t(debtFormTitle) }}</h4>
			<p class="text-base text-black font-medium">{{ data ? data.name : "" }}</p>
		</div>
		<perfect-scrollbar ref="vueScrollBar" class="scroll-area--data-list" :settings="settings">
			<div class="p-6">
				<!-- DeliveryCode -->
				<div class="manufacture-year">
					<label for class="vs-input--label">{{ $t("DeliveryCode") }}<span class="text-danger">*</span>:</label>
					<cbs-input
						v-model="deliveryCode"
						class="w-full"
						name="deliveryCode"
						:placeholder="`${$t('Actions.Input')} ${$t('DeliveryCode')}`"
						v-validate="'required'"
						:data-vv-as="$t('DeliveryCode')"
					/>
					<span class="text-danger text-sm break-normal" v-show="errors.has('deliveryCode')">{{ errors.first("deliveryCode") }}</span>
				</div>
				<!-- Products -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Products") }}<span class="text-danger">*</span>:</label>
					<v-select
						ref="selectProductsRef"
						v-model="productId"
						label="name"
						name="product"
						:options="productsChoices"
						:reduce="product => product.id"
						v-validate="'required'"
						:data-vv-as="$t('Products')"
						:placeholder="`${$t('Actions.Choose')} ${$t('Products').toLowerCase()}`"
						@search="fetchOptions"
						:filterable="false"
					>
						<span slot="no-options">{{ $t("Notify.NoMatchingOptions") }}</span>
					</v-select>
					<span class="text-danger text-sm break-normal" v-show="errors.has('product')">{{ errors.first("product") }}</span>
				</div>

				<!-- Issue Date -->
				<div class="deadline my-5">
					<label for class="vs-input--label">{{ $t("IssueDate") }}<span class="text-danger">*</span>:</label>

					<vx-input-group>
						<template slot="append">
							<div class="append-text text-black">
								<i class="nucleo nucleo-calendar-date" @click="triggerClickDatetimePicker"></i>
							</div>
						</template>

						<cbs-date-picker
							clearable
							class="w-full"
							type="datetime"
							name="start-date"
							prefix-icon="''"
							ref="issueDatePickerRef"
							format="DD-MM-yyyy HH:mm"
							value-format="yyyy-MM-dd HH:mm:ss"
							:data-vv-as="$t('IssueDate')"
							:placeholder="`${$t('Actions.Input')} ${$t('IssueDate').toLowerCase()}`"
							v-model="issuedDate"
							v-validate="'required'"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('start-date')">{{ errors.first("start-date") }}</span>
				</div>

				<!-- Amount -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Amount") }}<span class="text-danger">*</span>:</label>
					<vx-input-group prependClasses="border border-solid border-grey border-r-0">
						<template slot="prepend">
							<div class="prepend-text text-black">
								<span>VNĐ</span>
							</div>
						</template>
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="amount"
							name="amount"
							:userSetting="userSettings"
							v-validate="{ min_value: 0, required: true }"
							:data-vv-as="$t('Amount')"
							:placeholder="`${$t('Actions.InputMoney')}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('amount')">{{ errors.first("amount") }}</span>
				</div>
				<!-- Amount -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("PaidDays") }}<span class="text-danger">*</span>:</label>
					<vx-input-group prependClasses="border border-solid border-grey border-r-0">
						<template slot="prepend">
							<div class="prepend-text text-black">
								<span>Ngày</span>
							</div>
						</template>
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="paid_days"
							name="paiddays"
							:userSetting="userSettings"
							v-validate="{ min_value: 0, required: true }"
							:data-vv-as="$t('PaidDays')"
							:placeholder="`${$t('Actions.InputNumberOfDays')}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('paiddays')">{{ errors.first("paiddays") }}</span>
				</div>
			</div>
		</perfect-scrollbar>

		<div class="flex justify-center flex-wrap items-center" slot="footer">
			<vs-button :type="'border'" class="w-full sm:w-1/3 mb-3 sm:mb-0 sm:mr-6 font-bold" @click.stop="submitData(false)">
				{{ $t("Actions.SaveAndRecreate") }}
			</vs-button>
			<vs-button class="w-full sm:w-1/3 font-bold" @click.stop="submitData(true)">{{ $t("Actions.Save") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import _ from "lodash";
import vSelect from "vue-select";
import moment from "moment";

export default {
	name: "CustomerDebtFormSideBar",
	provide() {
		return { parentValidator: this.$validator };
	},
	props: {
		isSidebarActive: {
			type: Boolean,
			required: true
		},
		data: {
			type: Object,
			default: () => {}
		},
		debtFormTitle: {
			type: String,
			default: "CreateDebt"
		}
	},
	components: {
		vSelect
	},
	data() {
		return {
			deliveryCode: "",
			productId: "",
			issuedDate: "",
			amount: "",
			deadline: "",
			paid_days: "",
			isFormDirty: false,

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			}
		};
	},
	watch: {
		isSidebarActive(val) {
			if (!val) return;
			this.initValues();
		},
		data: function(newVal, oldVal) {
			if (_.isEmpty(newVal) || newVal.id !== oldVal.id) {
				this.$nextTick(() => {
					if (this.$refs.vueScrollBar) {
						this.$refs.vueScrollBar.$el.scrollTop = 0;
					}
				});
			}
		},
		computedFormData: {
			handler: function(newVal) {
				this.isFormDirty = JSON.stringify(newVal) !== JSON.stringify(this.initFormData);
			},
			deep: true
		},
		schedule_enable() {
			if (this.schedule_enable) {
				this.due_mode = 0;
			}
		}
	},

	computed: {
		isSidebarActiveLocal: {
			get() {
				return this.isSidebarActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeSidebar");
				}
			}
		},
		computedFormData() {
			return {};
		},
		productsChoices() {
			return this.$store.state.customerManagement.showProducts;
		}
	},
	methods: {
		initValues() {
			this.deliveryCode = "";
			this.productId = "";
			this.issuedDate = "";
			this.amount = "";
			this.deadline = "";
			this.paid_days = "";
			this.isFormDirty = false;
			this.setInitFormData();
			this.$validator.reset();
		},
		setInitFormData() {
			this.$nextTick(() => {
				this.initFormData = {
					deliveryCode: this.deliveryCode,
					productId: this.productId,
					issuedDate: this.issuedDate,
					amount: this.amount,
					deadline: this.deadline
				};
			});
		},
		handleCreateOrUpdateSuccess(isCloseForm) {
			if (isCloseForm) {
				this.$emit("closeSidebar");
			}
			this.initValues();
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: `${this.$t("CreateDebt")} ${this.$t("Successful")} `
				}
			});
		},
		async handleCreateOrUpdateCustomerDebt(dataObj, isCloseForm) {
			try {
				this.$vs.loading();
				let response;

				response = await this.$store.dispatch("customerManagement/createCustomerDebt", dataObj);

				if (response && response.id) {
					this.$emit("onCreateOrUpdateSuccess", response.id);
				}
				this.$store.dispatch("customerManagement/increaseTotalDebts");
				await this.$store.dispatch("customerManagement/getCustomerDetail", {
					id: this.data.id
				});

				this.handleCreateOrUpdateSuccess(isCloseForm);
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		submitData(isCloseForm = false) {
			this.$validator.validateAll().then(result => {
				if (result) {
					try {
						const dataObj = {
							amount: this.amount,
							product_id: this.productId,
							customer_id: this.data.id,
							delivery_code: this.deliveryCode,
							issued_date: this.issuedDate ? moment(this.issuedDate).toISOString() : null,
							paid_days: this.paid_days
						};

						this.handleCreateOrUpdateCustomerDebt(dataObj, isCloseForm);
					} catch (error) {
						console.error(error);
					}
				}
			});
		},
		triggerClickDatetimePicker() {
			this.$refs.issueDatePickerRef.$children[0].pickerVisible = true;
		},
		fetchOptions(search, loading) {
			clearTimeout(this.timer);
			this.timer = setTimeout(async () => {
				if (!loading) {
					loading = this.$refs[this.ref] && this.$refs[this.ref].toggleLoading;
				}

				loading(true);
				try {
					await this.fetchProducts(search);
					loading(false);
				} catch (error) {
					loading(false);
				}
			}, 400);
		},
		async fetchProducts(search = "") {
			const payload = { search: search, limit: 15, page: 1 };
			await this.$store.dispatch("customerManagement/fetchCustomerShowProducts", payload);
		}
	},
	created() {
		this.fetchProducts("a");
	}
};
</script>
<style lang="scss" scoped></style>
